document.addEventListener("alpine:init", () => {
  const MATACarousel = (config = {}) => ({
    optionsMobile: config.optionsMobile || {},
    optionsDesktop: config.optionsDesktop || {},
    renderMode: config.renderMode || "both",
    disableCarousel: Boolean(config.disableCarousel),
    flkty: null,
    mq: null,
    rootEl: null,
    visibilityObserver: null,
    init(rootEl) {
      this.rootEl = rootEl || this.rootEl;
      this.mq = window.matchMedia("(max-width: 1023px)");

      this.scheduleInit();

      const onMediaChange = () => this.scheduleInit();
      this._onMediaChange = onMediaChange;
      if (this.mq.addEventListener) {
        this.mq.addEventListener("change", onMediaChange);
      } else {
        this.mq.addListener(onMediaChange);
      }

      window.addEventListener(
        "load",
        () => {
          this.refreshFlickity();
        },
        { once: true }
      );
    },
    shouldDeferInit() {
      if (!this.rootEl || !("IntersectionObserver" in window)) return false;
      const rect = this.rootEl.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight || 0;
      return rect.top > viewportHeight + 300;
    },
    scheduleInit() {
      if (!this.rootEl) return;
      if (this.disableCarousel || !this.shouldInit() || !window.Flickity) {
        this.teardownVisibilityObserver();
        this.destroyFlickity();
        return;
      }
      if (this.flkty) {
        this.destroyFlickity();
      }
      if (this.shouldDeferInit()) {
        this.observeVisibility();
        return;
      }
      this.teardownVisibilityObserver();
      this.initFlickity();
    },
    observeVisibility() {
      if (!this.rootEl || !("IntersectionObserver" in window)) {
        this.initFlickity();
        return;
      }
      if (this.visibilityObserver) return;

      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.teardownVisibilityObserver();
            this.initFlickity();
          });
        },
        {
          rootMargin: "300px 0px",
        }
      );

      this.visibilityObserver.observe(this.rootEl);
    },
    teardownVisibilityObserver() {
      if (!this.visibilityObserver) return;
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    },
    buildOptions() {
      return (this.mq && this.mq.matches
        ? this.optionsMobile
        : this.optionsDesktop) || {};
    },
    shouldInit() {
      if (!this.mq) return true;
      if (this.renderMode === "mobile") return this.mq.matches;
      if (this.renderMode === "desktop") return !this.mq.matches;
      return true;
    },
    refreshFlickity() {
      if (!this.flkty) return;
      this.flkty.resize();
      this.flkty.reposition();
    },
    dispatchNavUpdate(rootEl = this.rootEl) {
      if (!this.flkty || !rootEl || typeof rootEl.dispatchEvent !== "function")
        return;
      rootEl.dispatchEvent(
        new CustomEvent("mata-carousel:update", {
          detail: {
            currentSlide: (this.flkty.selectedIndex || 0) + 1,
            totalSlides: this.flkty.slides ? this.flkty.slides.length : 1,
          },
        })
      );
    },
    destroyFlickity() {
      if (!this.flkty) return;
      clearTimeout(this.flkty._autoPlayRestart);
      this.flkty.destroy();
      this.flkty = null;
    },
    scheduleAutoPlay() {
      const autoPlayValue = this.flkty?.options?.autoPlay;
      if (!autoPlayValue) return;
      const delay =
        typeof autoPlayValue === "number"
          ? autoPlayValue
          : 3000;
      clearTimeout(this.flkty._autoPlayRestart);
      this.flkty._autoPlayRestart = setTimeout(() => {
        if (this.flkty && this.flkty.player) this.flkty.player.play();
      }, delay);
    },
    initFlickity(rootEl = this.rootEl) {
      if (!rootEl) return;
      if (this.flkty) return;
      if (this.disableCarousel || !this.shouldInit() || !window.Flickity) {
        this.destroyFlickity();
        return;
      }

      const options = Object.assign({}, this.buildOptions(), {
        cellSelector: ".mata-carousel__item",
      });

      this.destroyFlickity();
      this.flkty = new Flickity(rootEl, options);

      if (this.flkty?.options?.autoPlay && this.flkty.player) {
        this.flkty.player.play();
      }

      this.flkty.on("pointerDown", () => this.scheduleAutoPlay());
      this.flkty.on("uiChange", () => this.scheduleAutoPlay());
      this.flkty.on("dragEnd", () => this.scheduleAutoPlay());
      this.flkty.on("select", () => this.dispatchNavUpdate(rootEl));
      this.flkty.on("cellChange", () => this.dispatchNavUpdate(rootEl));

      this.dispatchNavUpdate(rootEl);
      requestAnimationFrame(() => this.refreshFlickity());
    },
    navPrev() {
      if (this.flkty) this.flkty.previous();
    },
    navNext() {
      if (this.flkty) this.flkty.next();
    },
  });

  const MATACarouselNav = () => ({
    currentSlide: 1,
    totalSlides: 1,
    rootEl: null,
    init(rootEl) {
      this.rootEl = rootEl || this.rootEl;
      if (!this.rootEl) return;

      const carouselEl = this.rootEl.previousElementSibling;
      if (!carouselEl) return;

      this._carouselEl = carouselEl;
      this._handleUpdate = (event) => {
        if (!event || !event.detail) return;
        this.currentSlide = event.detail.currentSlide || 1;
        this.totalSlides = event.detail.totalSlides || 1;
      };
      carouselEl.addEventListener("mata-carousel:update", this._handleUpdate);
    },
    getCarouselData(rootEl = this.rootEl) {
      if (!rootEl) return null;
      const navEl = rootEl.closest(".mata-carousel__nav");
      const carouselEl = navEl ? navEl.previousElementSibling : null;
      if (!carouselEl || !window.Alpine) return null;
      return Alpine.$data(carouselEl);
    },
    navPrev(rootEl) {
      const data = this.getCarouselData(rootEl);
      if (data && data.flkty) data.flkty.previous();
    },
    navNext(rootEl) {
      const data = this.getCarouselData(rootEl);
      if (data && data.flkty) data.flkty.next();
    },
  });

  window.MATACarousel = MATACarousel;
  window.MATACarouselNav = MATACarouselNav;
  Alpine.data("MATACarousel", MATACarousel);
  Alpine.data("MATACarouselNav", MATACarouselNav);
});
